import React, { useState, useRef } from "react";
import { bool, func } from "prop-types";
import { useTranslation } from "@lib/i18n";

import { Button } from "@components/shared/Button";
import Modal from "@components/shared/Modal";
import Checkbox from "@components/shared/Checkbox";
import TextField from "@components/shared/TextField";
import ButtonGroup from "@components/shared/ButtonGroup";

import { sendDocuments } from "@lib/sendDocuments";
import { toast } from "@lib/reactToastify";
import { getIsCountry, COUNTRIES } from "@lib/getIsCountry";
import { shareDocumentsModalEvent } from "@gtm/events/shared";
import { useBrandCodeSelector } from "@redux/reducers/appConfig";

import gtm from "@gtm/core";

import {
  useIsLOAJourneyEnabledSelector,
  useIsLLDJourneyEnabledSelector,
  useIsVACJourneyEnabledSelector,
} from "@redux/reducers/appConfig";

const ShareInfoModal = ({ isOpen, closeModal }) => {
  const { t } = useTranslation();
  const brandCode = useBrandCodeSelector();
  const isLOAJourneyEnabled = useIsLOAJourneyEnabledSelector();
  const isLLDJourneyEnabled = useIsLLDJourneyEnabledSelector();
  const isVACJourneyEnabled = useIsVACJourneyEnabledSelector();

  const [isCheckedTermsOfService, setIsCheckedTermsOfService] = useState(false);
  const [isCheckedTermsOfSales, setIsCheckedTermsOfSales] = useState(false);
  const [isCheckedWarranty, setIsCheckedWarranty] = useState(false);
  const [isCheckedAssistance, setIsCheckedAssistance] = useState(false);
  const [isCheckedService, setIsCheckedService] = useState(false);
  const [isCheckedGuarantee, setIsCheckedGuarantee] = useState(false);
  const [isCheckedLOAConditions, setIsCheckedLOAConditions] = useState(false);
  const [isCheckedVACConditions, setIsCheckedVACConditions] = useState(false);
  const [email, setEmail] = useState("");
  const [isSending, setIsSending] = useState(false);

  const modalFirstElement = useRef(null);
  const modalLastElement = useRef(null);

  const cancelButtonText = t("general.cancel");
  const sendButtonText = t("general.send");
  const isDocumentSelected =
    isCheckedTermsOfService ||
    isCheckedTermsOfSales ||
    isCheckedService ||
    isCheckedGuarantee ||
    isCheckedLOAConditions ||
    isCheckedVACConditions ||
    isCheckedWarranty ||
    isCheckedAssistance;

  const shareDocuments = async e => {
    e.preventDefault();

    if (isSending) {
      return;
    }

    setIsSending(true);

    try {
      await sendDocuments(
        email,
        brandCode,
        +isCheckedTermsOfService,
        +isCheckedTermsOfSales,
        +isCheckedWarranty,
        +isCheckedAssistance,
        +isCheckedService,
        +isCheckedGuarantee,
        +isCheckedLOAConditions,
        +isCheckedVACConditions
      );

      toast.success(t("basket.shareConfigurationModal.notification"));

      gtm.fire(
        shareDocumentsModalEvent({
          label: sendButtonText,
          envoyer: true,
        })
      );

      closeModal();
    } catch {
      toast.error(t("basket.shareConfigurationModal.error"));
    }

    setIsSending(false);
  };

  const onCancelButtonClick = () => {
    gtm.fire(
      shareDocumentsModalEvent({
        label: cancelButtonText,
        envoyer: false,
      })
    );

    closeModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      firstElement={modalFirstElement}
      lastElement={modalLastElement}
      ariaLabel={t("footer.shareInformationModal.heading")}
      onClose={closeModal}
      closeButtonDataId="seller-information-close-icon"
      withCloseIcon
    >
      <form
        className="flex flex-col items-start h-full gap-4"
        data-id="share-information-modal"
        onSubmit={shareDocuments}
      >
        <h4 className="text-xl font-semibold">
          {t("footer.shareInformationModal.heading")}
        </h4>
        <p>{t("footer.shareInformationModal.text")}</p>
        <div className="flex flex-col gap-2 w-full">
          {!getIsCountry(COUNTRIES.GB) && (
            <Checkbox
              checked={isCheckedTermsOfService}
              onChange={() =>
                setIsCheckedTermsOfService(!isCheckedTermsOfService)
              }
              label={t("footer.termsOfService")}
            />
          )}

          {t("footer.termsOfSales") && t("footer.termsOfSales").length > 0 && (
            <Checkbox
              checked={isCheckedTermsOfSales}
              onChange={() => setIsCheckedTermsOfSales(!isCheckedTermsOfSales)}
              label={t("footer.termsOfSales")}
            />
          )}
          <Checkbox
            checked={isCheckedService}
            onChange={() => setIsCheckedService(!isCheckedService)}
            label={t("footer.serviceConditions")}
          />
          {isLLDJourneyEnabled &&
            t("footer.deliveryGuaranteeConditions") &&
            t("footer.deliveryGuaranteeConditions").length > 0 && (
              <Checkbox
                checked={isCheckedGuarantee}
                onChange={() => setIsCheckedGuarantee(!isCheckedGuarantee)}
                label={t("footer.deliveryGuaranteeConditions")}
              />
            )}
          {isLOAJourneyEnabled &&
            t("footer.loaConditions") &&
            t("footer.loaConditions").length > 0 && (
              <Checkbox
                checked={isCheckedLOAConditions}
                onChange={() =>
                  setIsCheckedLOAConditions(!isCheckedLOAConditions)
                }
                label={t("footer.loaConditions")}
              />
            )}
          {isVACJourneyEnabled &&
            t("footer.vacConditions") &&
            t("footer.vacConditions").length > 0 && (
              <Checkbox
                checked={isCheckedVACConditions}
                onChange={() =>
                  setIsCheckedVACConditions(!isCheckedVACConditions)
                }
                label={t("footer.vacConditions")}
              />
            )}
          {t("footer.termsOfWarranty") &&
            t("footer.termsOfWarranty").length > 0 && (
              <Checkbox
                checked={isCheckedWarranty}
                onChange={() => setIsCheckedWarranty(!isCheckedWarranty)}
                label={t("footer.termsOfWarranty")}
              />
            )}
          {t("footer.termsOfAssistance") &&
            t("footer.termsOfAssistance").length > 0 && (
              <Checkbox
                checked={isCheckedAssistance}
                onChange={() => setIsCheckedAssistance(!isCheckedAssistance)}
                label={t("footer.termsOfAssistance")}
              />
            )}
        </div>
        <TextField
          className="m-0 min-w-60"
          data-id="personal-details-name"
          fullWidth
          id="email"
          label={t("footer.shareInformationModal.email")}
          onChange={e => setEmail(e.target.value)}
          placeholder={t("footer.shareInformationModal.email")}
          required
          type="email"
          value={email}
        />
        <ButtonGroup className="">
          <Button
            data-id="footer-share-send-btn"
            disabled={!isDocumentSelected}
            size="small"
            type="submit"
            variant="primary"
          >
            {sendButtonText}
          </Button>
          <Button
            data-id="footer-share-docs-cancel-btn"
            onClick={onCancelButtonClick}
            ref={modalLastElement}
            size="small"
            type="reset"
            variant="secondary"
          >
            {cancelButtonText}
          </Button>
        </ButtonGroup>
      </form>
    </Modal>
  );
};

ShareInfoModal.propTypes = {
  isOpen: bool.isRequired,
  closeModal: func.isRequired,
};

export default ShareInfoModal;
